import type { AuthAdapter, AdapterResult } from "./types";
import type { AuthProvider, BridgeLoginResult } from "../types";
import type { BridgeV1AuthState } from "../bridge/protocol";
import { BRIDGE_ALLOWED_ORIGINS } from "../bridge/protocol";

const BRIDGE_TIMEOUT_MS = 60_000;

function generateRequestId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

interface PendingRequest {
  resolve: (idToken: string) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export class NativeBridgeAdapter implements AuthAdapter {
  readonly provider: AuthProvider;
  private pendingRequests = new Map<string, PendingRequest>();
  private messageHandler: ((event: MessageEvent) => void) | null = null;

  constructor(provider: AuthProvider) {
    this.provider = provider;
    if (typeof window !== "undefined") {
      this.messageHandler = this.handleMessage.bind(this);
      window.addEventListener("message", this.messageHandler);
    }
  }

  async authenticate(): Promise<AdapterResult> {
    const requestId = generateRequestId();
    const idToken = await this.sendAndWait(requestId);
    return { authHeaders: { "OS-Firebase-Token": idToken } };
  }

  cancel(): void {
    for (const [id, pending] of this.pendingRequests) {
      clearTimeout(pending.timer);
      pending.reject(new Error("Cancelled"));
      this.pendingRequests.delete(id);
    }
  }

  destroy(): void {
    this.cancel();
    if (this.messageHandler && typeof window !== "undefined") {
      window.removeEventListener("message", this.messageHandler);
      this.messageHandler = null;
    }
  }

  private sendAndWait(requestId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error("Native bridge timeout"));
      }, BRIDGE_TIMEOUT_MS);

      this.pendingRequests.set(requestId, { resolve, reject, timer });

      if ((window as any).webkit?.messageHandlers?.bridge) {
        // iOS WKWebView expects a native JS object with { type, id, data }
        const message = {
          type: "login",
          id: requestId,
          data: {
            provider: this.provider.toUpperCase(),
            version: "2.0",
            requestId,
          },
        };
        (window as any).webkit.messageHandlers.bridge.postMessage(message);
      } else if ((window as any).bridge?.login) {
        // Android expects a JSON string
        const payload = JSON.stringify({
          type: "login",
          action: "login",
          requestId,
          provider: this.provider.toUpperCase(),
          version: "2.0",
        });
        (window as any).bridge.login(payload);
      } else {
        clearTimeout(timer);
        this.pendingRequests.delete(requestId);
        reject(new Error("Native bridge not available"));
      }
    });
  }

  private handleMessage(event: MessageEvent): void {
    let data = event.data;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch {
        return;
      }
    }
    if (!data || typeof data !== "object") {
      return;
    }

    // v2: requestId 本身提供认证，无需 origin 校验
    if (data.type === "auth-login-result" && data.requestId) {
      if (this.pendingRequests.has(data.requestId)) {
        this.handleV2Response(data as BridgeLoginResult);
      }
      return;
    }

    // v1 fallback: 仅接受白名单 origin
    if (data.type === "firebase-auth-state") {
      if (
        BRIDGE_ALLOWED_ORIGINS.includes(
          event.origin as (typeof BRIDGE_ALLOWED_ORIGINS)[number],
        )
      ) {
        this.handleV1Fallback(data as BridgeV1AuthState);
      }
    }
  }

  private handleV2Response(data: BridgeLoginResult): void {
    const pending = this.pendingRequests.get(data.requestId);
    if (!pending) {
      return;
    }

    clearTimeout(pending.timer);
    this.pendingRequests.delete(data.requestId);

    if (data.success && data.idToken) {
      pending.resolve(data.idToken);
    } else {
      // 带上壳回传的 error.code：埋点据此区分「用户取消」与真实失败（authTracking.isAuthCancelled）
      const error: Error & { code?: string } = new Error(
        data.error?.message ?? "Native login failed",
      );
      if (data.error?.code) {
        error.code = data.error.code;
      }
      pending.reject(error);
    }
  }

  private handleV1Fallback(data: BridgeV1AuthState): void {
    if (data.action !== "login" || !data.idToken) {
      return;
    }
    if (this.pendingRequests.size === 0) {
      return;
    }

    // 取第一个 pending request 匹配（v1 无 requestId 只能这样）
    const [id, pending] = this.pendingRequests.entries().next().value!;
    clearTimeout(pending.timer);
    this.pendingRequests.delete(id);
    pending.resolve(data.idToken);
  }
}
